import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  AutoComplete,
  DatePicker,
  Upload,
  Button,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import moment from 'moment';
import costsClaimApi from 'api/costsClaimApi';
import employeeApi from 'api/employeeApi';

const { Option } = Select;
const { TextArea } = Input;

interface ClaimFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any, fileList: any[]) => void;
  initialValues?: any;
  expenseTypes: any[];
  paymentMethods: any[];
  banks: any[];
  loading?: boolean;
}

export const ClaimFormModal: React.FC<ClaimFormModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  expenseTypes,
  paymentMethods,
  banks,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<any[]>([]);

  // Fetch employee list from DB for CC email dropdown
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res: any = await employeeApi.getEmployeeListNotifyLeaveOff();
        if (res?.statusCode === 200 || res?.data?.result) {
          const list = res.data?.result || res.result || [];
          setEmployeeOptions(list);
        }
      } catch (err) {
        // Fallback silently if API fails
      }
    };
    if (visible) {
      fetchEmployees();
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        const foundEt = expenseTypes.find((et) => et.id === initialValues.expenseTypeId);
        const parsedCc = initialValues.ccEmails
          ? String(initialValues.ccEmails)
              .split(/[,;]/)
              .map((e) => e.trim())
              .filter(Boolean)
          : [];

        form.setFieldsValue({
          title: initialValues.title,
          description: initialValues.description,
          amount: initialValues.amount,
          currency: initialValues.currency || 'VND',
          expenseTypeValue: foundEt ? foundEt.nameVi || foundEt.code : undefined,
          paymentMethodId: initialValues.paymentMethodId,
          bankId: initialValues.bankId,
          claimDate: initialValues.claimDate ? moment(initialValues.claimDate) : moment(),
          targetAccountNumber: initialValues.targetAccountNumber,
          beneficiaryName: initialValues.beneficiaryName,
          ccEmails: parsedCc,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ currency: 'VND', claimDate: moment() });
      }
      setFileList([]);
    }
  }, [visible, initialValues, form, expenseTypes]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      // Handle custom or selected expense type
      let resolvedExpenseTypeId: number | undefined = undefined;
      const typedExpenseVal = values.expenseTypeValue?.trim();

      if (typedExpenseVal) {
        const matchedEt = expenseTypes.find(
          (et) =>
            et.nameVi?.toLowerCase() === typedExpenseVal.toLowerCase() ||
            et.code?.toLowerCase() === typedExpenseVal.toLowerCase(),
        );

        if (matchedEt) {
          resolvedExpenseTypeId = matchedEt.id;
        } else {
          try {
            const cleanCode = 'EXP_' + Date.now();
            const res: any = await costsClaimApi.createExpenseType({
              nameVi: typedExpenseVal,
              nameEn: typedExpenseVal,
              code: cleanCode,
            });
            resolvedExpenseTypeId = res?.data?.id || res?.id;
          } catch (createErr) {
            // Fallback
          }
        }
      }

      // Format CC Emails array into comma-separated string
      const formattedCc = Array.isArray(values.ccEmails)
        ? values.ccEmails.join(',')
        : values.ccEmails || '';

      const { expenseTypeValue, ...restValues } = values;
      const formattedValues = {
        ...restValues,
        ccEmails: formattedCc,
        expenseTypeId: resolvedExpenseTypeId,
        claimDate: values.claimDate ? values.claimDate.format('YYYY-MM-DD') : undefined,
      };

      onSubmit(formattedValues, fileList);
    } catch (err) {
      // Form validation failed
    }
  };

  const uploadProps = {
    onRemove: (file: any) => {
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
    },
    beforeUpload: (file: any) => {
      setFileList((prev) => [...prev, file]);
      return false;
    },
    fileList,
  };

  const selectedPaymentMethodId = Form.useWatch('paymentMethodId', form);
  const selectedMethod = paymentMethods.find((pm) => pm.id === selectedPaymentMethodId);
  const isCash =
    selectedMethod?.code === 'CASH' || selectedMethod?.nameVi?.toLowerCase().includes('tiền mặt');

  useEffect(() => {
    if (isCash) {
      form.setFieldsValue({
        bankId: undefined,
        targetAccountNumber: undefined,
        beneficiaryName: undefined,
      });
    }
  }, [isCash, form]);

  const expenseTypeOptions = expenseTypes.map((item) => ({
    value: item.nameVi || item.code,
    label: item.nameVi || item.code,
    id: item.id,
  }));

  return (
    <Modal
      title={initialValues ? 'Chỉnh sửa Claim Chi Phí' : 'Tạo mới Claim Chi Phí'}
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      width={700}
      destroyOnClose
      okText={initialValues ? 'Lưu thay đổi' : 'Tạo mới'}
      cancelText="Hủy"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="title"
          label="Tiêu đề Yêu cầu"
          rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
        >
          <Input placeholder="Ví dụ: Chi phí công tác Hà Nội T8/2026" maxLength={500} />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            name="amount"
            label="Số tiền"
            rules={[{ required: true, message: 'Vui lòng nhập số tiền' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
              min={1000}
              placeholder="500,000"
            />
          </Form.Item>

          <Form.Item name="currency" label="Loại tiền" initialValue="VND">
            <Select getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}>
              <Option value="VND">VND</Option>
              <Option value="USD">USD</Option>
              <Option value="EUR">EUR</Option>
            </Select>
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            name="claimDate"
            label="Ngày phát sinh chi phí"
            rules={[{ required: true, message: 'Vui lòng chọn ngày' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item name="expenseTypeValue" label="Loại chi phí (Chọn hoặc tự nhập)">
            <AutoComplete
              options={expenseTypeOptions}
              placeholder="Chọn hoặc tự nhập loại chi phí bất kỳ..."
              filterOption={(inputValue, option: any) =>
                option?.value?.toLowerCase().includes(inputValue.toLowerCase())
              }
              getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
            />
          </Form.Item>
        </div>

        <Form.Item name="description" label="Mô tả chi tiết">
          <TextArea rows={3} placeholder="Mô tả chi tiết về lý do phát sinh chi phí..." />
        </Form.Item>

        <div
          style={{ display: 'grid', gridTemplateColumns: isCash ? '1fr' : '1fr 1fr', gap: '16px' }}
        >
          <Form.Item name="paymentMethodId" label="Phương thức thanh toán">
            <Select
              placeholder="Chọn phương thức"
              allowClear
              getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
            >
              {paymentMethods.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.nameVi || item.code}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {!isCash && (
            <Form.Item name="bankId" label="Ngân hàng nhận tiền">
              <Select
                placeholder="Chọn ngân hàng"
                allowClear
                showSearch
                optionFilterProp="children"
                getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
              >
                {banks.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.code} - {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </div>

        {!isCash && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="targetAccountNumber" label="Số tài khoản nhận tiền">
              <Input placeholder="1903..." />
            </Form.Item>

            <Form.Item name="beneficiaryName" label="Tên người thụ hưởng">
              <Input placeholder="NGUYEN VAN A" />
            </Form.Item>
          </div>
        )}

        <Form.Item name="ccEmails" label="CC Email thông báo">
          <Select
            mode="tags"
            allowClear
            style={{ width: '100%' }}
            placeholder="Chọn nhân viên từ hệ thống HRM hoặc gõ email bất kỳ..."
            tokenSeparators={[',', ';']}
            filterOption={(inputValue, option: any) =>
              String(option?.children || '')
                .toLowerCase()
                .includes(inputValue.toLowerCase()) ||
              String(option?.value || '')
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
            getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
          >
            {employeeOptions.map((emp) => {
              const displayVal = emp.email || emp.username || emp.fullName;
              if (!displayVal) return null;
              return (
                <Option key={emp.id || displayVal} value={displayVal}>
                  {emp.fullName ? `${emp.fullName} (${displayVal})` : displayVal}
                </Option>
              );
            })}
          </Select>
        </Form.Item>

        <Form.Item label="Chứng từ / Hóa đơn đính kèm">
          <Upload {...uploadProps} multiple>
            <Button icon={<UploadOutlined />}>Tải lên file hóa đơn (PDF, PNG, JPG)</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ClaimFormModal;
